/**
 * FirstRunGuideContext — durable first-run activation guide.
 *
 * Mounted once near the App root (inside the tenant/profile providers, above the
 * router), so its state SURVIVES route changes — navigating never resets or
 * closes the guide. State is persisted per tenant via a swappable GuideStore
 * (localStorage today; a DB-backed store can replace it without touching this
 * file's consumers).
 *
 * The guide only fully disappears when the user explicitly dismisses it OR the
 * workspace is meaningfully activated (all required steps done). A dismissed
 * guide collapses to a reopenable pill rather than vanishing. On completion it
 * shows a one-time celebration, then retires permanently — CommandCentre/People
 * already show live priorities, so retiring is a handoff, not a disappearance.
 */

import {
  createContext, useContext, useState, useEffect, useCallback, useMemo, useRef,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCurrentTenantId } from './TenantContext'
import { useProfile } from './ProfileContext'
import {
  GUIDE_STEPS,
  defaultGuideState,
  emptySignals,
  resolveActiveIndex,
  firstIncompleteIndex,
  isStepDone,
  allRequiredDone,
  doneCount,
  computeHasLaunched,
  findLaunchCandidate,
  localStorageGuideStore,
  type GuideStep,
  type GuideSignals,
  type GuidePersistedState,
  type GuideStore,
} from '../lib/firstRunGuide'

interface FirstRunGuideValue {
  /** Whether the floating card should render at all (active card, dismissed pill, or the one-time completion card). */
  enabled: boolean
  status: GuidePersistedState['status']
  /** True for exactly one render cycle per tenant: setup just finished and hasn't been acknowledged yet. */
  justCompleted: boolean
  steps: GuideStep[]
  signals: GuideSignals
  /** The step the user should act on next. */
  activeIndex: number
  activeStep: GuideStep
  /** The first hire still waiting to be launched, if any — lets the "launch" step's CTA open their profile directly. */
  launchCandidate: { id: string; name: string } | null
  /** The first not-yet-done step, derived fresh from signals — the boundary future steps can't cross. */
  leadingEdge: number
  doneCount: number
  totalSteps: number
  isStepDone: (step: GuideStep) => boolean
  /** Re-fetch signals immediately (e.g. right after a mutation elsewhere completes) instead of waiting for the next window-focus tick. */
  refreshSignals: () => Promise<void>
  /** Advance to the next step (marks the current soft step actioned). */
  next: () => void
  prev: () => void
  /** Jump to a completed step to review it, or to the leading edge to resume. Locked steps beyond the edge are refused. */
  goToStep: (index: number) => void
  /** Run a step CTA: mark it actioned, advance, then navigate. Never dismisses. */
  runCta: (step: GuideStep) => void
  /** Hide the guide to a reopenable pill (explicit user action). */
  dismiss: () => void
  /** Bring the guide back from the pill / from a "Setup guide" entry point. */
  reopen: () => void
  /** Acknowledge the one-time completion card — after this the guide never returns for this tenant. */
  acknowledgeCompletion: () => void
}

const Ctx = createContext<FirstRunGuideValue | undefined>(undefined)

const NEW_TENANT_WINDOW_MS = 14 * 24 * 60 * 60 * 1000 // 14 days

export function FirstRunGuideProvider({
  children,
  store = localStorageGuideStore,
}: {
  children: ReactNode
  /** Injectable for tests / future DB-backed persistence. */
  store?: GuideStore
}) {
  const tenantId = useCurrentTenantId()
  const { profile } = useProfile()
  const navigate = useNavigate()

  const [persisted, setPersisted] = useState<GuidePersistedState>(defaultGuideState)
  const [signals, setSignals] = useState<GuideSignals>(emptySignals)
  const [launchCandidate, setLaunchCandidate] = useState<{ id: string; name: string } | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const [signalsLoaded, setSignalsLoaded] = useState(false)
  const loadedTenant = useRef<string | null>(null)

  // ── Load persisted state when the tenant becomes known ─────────────────────
  useEffect(() => {
    if (!tenantId) return
    if (loadedTenant.current === tenantId) return
    loadedTenant.current = tenantId
    setPersisted(store.load(tenantId) ?? defaultGuideState())
    setHydrated(true)
  }, [tenantId, store])

  // ── Persist on change ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!tenantId || !hydrated) return
    store.save(tenantId, persisted)
  }, [tenantId, hydrated, persisted, store])

  // ── Fetch activation signals (same sources as ActivationChecklist) ─────────
  const refreshSignals = useCallback(async () => {
    if (!tenantId) return
    const [
      blueprintRes, employeeRes, launchedTasksRes, forecastRes, memberRes,
      employeesRes, launchedTaskIdsRes, journeysRes, journeyEmployeeIdsRes,
    ] = await Promise.all([
      supabase.from('blueprints').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).eq('status', 'published').limit(1),
      supabase.from('employees').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).limit(1),
      // Legacy signal: a task was assigned directly to an employee.
      supabase.from('tasks').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).not('employee_id', 'is', null).limit(1),
      supabase.from('journeys').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).not('forecast_state', 'is', null).limit(1),
      supabase.from('profiles').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).eq('role', 'member').limit(1),
      // Candidate for the "launch" step's deep link: the first hire still
      // waiting to be launched (used so its CTA opens their profile directly
      // instead of just landing on the People list).
      supabase.from('employees').select('id, name')
        .eq('tenant_id', tenantId).order('created_at', { ascending: true }).limit(20),
      supabase.from('tasks').select('employee_id')
        .eq('tenant_id', tenantId).not('employee_id', 'is', null).limit(500),
      // Current signal: applying a blueprint creates a `journeys` row (with
      // `journey_steps`), not legacy `tasks` rows — the launch step must key
      // off this, not just the legacy path above.
      supabase.from('journeys').select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId).limit(1),
      supabase.from('journeys').select('employee_id')
        .eq('tenant_id', tenantId).limit(500),
    ])
    setSignals({
      hasBlueprint: (blueprintRes.count ?? 0) > 0,
      hasEmployee: (employeeRes.count ?? 0) > 0,
      hasLaunched: computeHasLaunched({
        legacyLaunchedTaskCount: launchedTasksRes.count ?? 0,
        journeyCount: journeysRes.count ?? 0,
      }),
      hasReadinessForecast: (forecastRes.count ?? 0) > 0,
      hasTeamMember: (memberRes.count ?? 0) > 0,
    })
    const launchedIds = [
      ...(launchedTaskIdsRes.data ?? []).map((r) => r.employee_id as string),
      ...(journeyEmployeeIdsRes.data ?? []).map((r) => r.employee_id as string),
    ]
    setLaunchCandidate(findLaunchCandidate(
      (employeesRes.data ?? []).map((e) => ({ id: e.id as string, name: e.name as string })),
      launchedIds,
    ))
    setSignalsLoaded(true)
  }, [tenantId])

  useEffect(() => { void refreshSignals() }, [refreshSignals])

  // Re-check signals when the user returns to the tab (e.g. after creating a
  // hire on another route) so completed steps tick over without a full reload.
  useEffect(() => {
    const onFocus = () => { void refreshSignals() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshSignals])

  // ── Auto-complete when the workspace is meaningfully activated ─────────────
  useEffect(() => {
    if (!hydrated || persisted.status !== 'active') return
    if (allRequiredDone(signals, persisted.visited)) {
      setPersisted(p => (p.status === 'completed' ? p : { ...p, status: 'completed' }))
    }
  }, [hydrated, signals, persisted.status, persisted.visited])

  // ── Is this a new tenant who should see the guide? ─────────────────────────
  const isNewTenant = useMemo(() => {
    const isAdmin = profile?.role === 'owner' || profile?.role === 'admin'
    if (!isAdmin) return false
    const created = profile?.created_at ? new Date(profile.created_at) : null
    const withinWindow = !created || (Date.now() - created.getTime()) < NEW_TENANT_WINDOW_MS
    return withinWindow
  }, [profile])

  const leadingEdge = useMemo(
    () => firstIncompleteIndex(signals, persisted.visited),
    [signals, persisted.visited],
  )

  const activeIndex = useMemo(
    () => resolveActiveIndex(persisted, signals),
    [persisted, signals],
  )

  /** Mark the currently-viewed step actioned (if soft-completable) and resume following the leading edge. */
  const advance = useCallback(() => {
    setPersisted(p => {
      const cur = GUIDE_STEPS[activeIndex]
      const visited = cur?.softComplete && !p.visited.includes(cur.id) ? [...p.visited, cur.id] : p.visited
      return { ...p, pinned: false, currentStep: firstIncompleteIndex(signals, visited), visited }
    })
  }, [activeIndex, signals])

  const next = advance

  const prev = useCallback(() => {
    const target = activeIndex - 1
    if (target < 0) return
    setPersisted(p => ({ ...p, pinned: true, currentStep: target }))
  }, [activeIndex])

  /** Completed steps are revisit-able (pins the view); steps beyond the leading edge are locked and refused. */
  const goToStep = useCallback((index: number) => {
    if (index > leadingEdge) return
    setPersisted(p => (
      index >= leadingEdge
        ? { ...p, pinned: false, currentStep: leadingEdge }
        : { ...p, pinned: true, currentStep: index }
    ))
  }, [leadingEdge])

  const runCta = useCallback((step: GuideStep) => {
    // Mark soft steps actioned, resume following the edge, THEN navigate.
    // Critically: this never dismisses — the guide persists on the new route.
    advance()
    // The "launch" step has no single generic destination — launching means
    // opening a specific hire's profile and applying a blueprint there, so
    // route straight to the hire still waiting on it when we know who that is.
    if (step.id === 'launch' && launchCandidate) {
      navigate(`/people/${launchCandidate.id}?action=apply-blueprint`)
    } else if (step.ctaPath) {
      navigate(step.ctaPath)
    }
  }, [advance, navigate, launchCandidate])

  const dismiss = useCallback(() => {
    setPersisted(p => ({ ...p, status: 'dismissed' }))
  }, [])

  const reopen = useCallback(() => {
    setPersisted(p => ({ ...p, status: p.status === 'completed' ? 'completed' : 'active' }))
  }, [])

  const acknowledgeCompletion = useCallback(() => {
    setPersisted(p => ({ ...p, celebrated: true }))
  }, [])

  const justCompleted = persisted.status === 'completed' && !persisted.celebrated

  const value: FirstRunGuideValue = {
    enabled: hydrated && signalsLoaded && isNewTenant && !(persisted.status === 'completed' && persisted.celebrated),
    status: persisted.status,
    justCompleted,
    steps: GUIDE_STEPS,
    signals,
    activeIndex,
    activeStep: GUIDE_STEPS[activeIndex],
    launchCandidate,
    leadingEdge,
    doneCount: doneCount(signals, persisted.visited),
    totalSteps: GUIDE_STEPS.length,
    isStepDone: (step) => isStepDone(step, signals, persisted.visited),
    refreshSignals,
    next,
    prev,
    goToStep,
    runCta,
    dismiss,
    reopen,
    acknowledgeCompletion,
  }

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useFirstRunGuide(): FirstRunGuideValue {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useFirstRunGuide must be used within FirstRunGuideProvider')
  return ctx
}
